// WizardMessagesSection — US-129 (message sequence editor).
//
// Extracted verbatim from CreateWizard's inline "Section 5 — messages" block.

import { useT } from '@barel/i18n';
import { Banner, Button, Card } from '@barel/ui';

import { MessageSequenceEditor, type MessageSequenceValue } from '../MessageSequenceEditor';
import type { CampaignSequenceValidation } from '../lib/sequence';

export interface WizardMessagesSectionProps {
  sequence: MessageSequenceValue;
  validation: CampaignSequenceValidation;
  onSequenceChange: (next: MessageSequenceValue) => void;
  onGenerate: () => void | Promise<void>;
  canGenerate: boolean;
  isGenerating: boolean;
  generationErrorCode?: string;
  generatedExampleCount?: number;
}

export function WizardMessagesSection({
  sequence,
  validation,
  onSequenceChange,
  onGenerate,
  canGenerate,
  isGenerating,
  generationErrorCode,
  generatedExampleCount,
}: WizardMessagesSectionProps) {
  const t = useT('campaigns');
  const generationErrorKey =
    generationErrorCode === 'ai.budget_exceeded' || generationErrorCode === 'ai.call_quota_exceeded'
      ? 'create.sectionMessages.generation.quotaError'
      : generationErrorCode === 'ai.unavailable' || generationErrorCode === 'ai.invalid_response'
        ? 'create.sectionMessages.generation.unavailableError'
        : 'create.sectionMessages.generation.genericError';
  return (
    <Card padding="lg" radius="2xl" data-testid="wizard-section-messages">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <strong className="text-primary text-sm">{t('create.sectionMessages.title')}</strong>
          <p className="text-tertiary text-xs mt-1">{t('create.sectionMessages.sub')}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={!canGenerate || isGenerating}
          loading={isGenerating}
          onClick={() => void onGenerate()}
          data-testid="campaign-generate-sequence"
        >
          {isGenerating
            ? t('create.sectionMessages.generation.loading')
            : t('create.sectionMessages.generation.action')}
        </Button>
      </div>
      {!canGenerate ? (
        <p className="mb-3 text-tertiary text-xs">
          {t('create.sectionMessages.generation.selectContext')}
        </p>
      ) : null}
      {generationErrorCode ? (
        <Banner
          tone="error"
          variant="soft"
          className="mb-3"
          data-testid="campaign-generation-error"
        >
          {t(generationErrorKey)}
        </Banner>
      ) : null}
      {generatedExampleCount !== undefined ? (
        <Banner
          tone="success"
          variant="soft"
          className="mb-3"
          data-testid="campaign-generation-evidence"
        >
          {generatedExampleCount > 0
            ? t('create.sectionMessages.generation.evidence', {
                count: generatedExampleCount,
              })
            : t('create.sectionMessages.generation.noExamples')}
        </Banner>
      ) : null}
      <MessageSequenceEditor value={sequence} onChange={onSequenceChange} />
      {!validation.valid ? (
        <Banner
          tone="warning"
          variant="soft"
          className="mt-3"
          data-testid="campaign-sequence-validation"
        >
          <ul className="m-0 pl-4">
            {validation.codes.map((code) => (
              <li key={code}>{t(`create.sectionMessages.validation.${code}`)}</li>
            ))}
          </ul>
        </Banner>
      ) : null}
    </Card>
  );
}
